import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Formatting for a study's acquisition date.
 *
 * Always rendered in UTC, deliberately. The backend builds study_date from DICOM
 * StudyDate + StudyTime, which carry no timezone, and interprets that wall clock
 * as UTC. Rendering it in the viewer's local zone would shift the instant: a
 * study with no StudyTime is stored at midnight UTC, so any negative offset
 * shows the *previous day* — a US/Eastern reader would see "Sep 26" for a
 * 20180927 study, while the report header (which formats server-side in UTC)
 * says "Sep 27". Formatting in UTC everywhere keeps the rendered date equal to
 * the date on the scanner console, which is the whole point of the column.
 *
 * Centralising it here also gives the date one owner, so a new read path can't
 * quietly go back to createdAt (upload time).
 */

/** "Jan 5, 2026" in UTC. */
export function formatStudyDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = dayjs.utc(iso);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
}

/** "Jan 5, 2026, 2:30 PM" in UTC — the time shown is the scanner's clock. */
export function formatStudyDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = dayjs.utc(iso);
  return d.isValid() ? d.format('MMM D, YYYY, h:mm A') : '—';
}

/** Sortable UTC day key, e.g. for CSV export or grouping. */
export function studyDateKey(iso?: string | null): string {
  if (!iso) return '';
  const d = dayjs.utc(iso);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
}
